/**
 * This file defines the fetchAccessRequestsCapabilities() service.
 * https://hello.atlassian.net/wiki/spaces/ETRUST/pages/626729299/API+Guide+-+v1+access-requests+capabilities
 */
import { OPERATIONAL_EVENT_TYPE, type GasCorePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { AnyAri } from '@atlassian/ari/any-ari';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	type EnablementTouchpoint,
	type GetAdditionalAttributes,
	getAnalyticsChannel,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getStargateURL,
	type Product,
} from '../common/common';
import { type FetchInput, type FetchResponse, fetchJSON } from '../common/fetchJSON';
import {
	type AnalyticsPayload,
	fireErrorAnalytics as sharedFireErrorAnalytics,
} from '../common/fireErrorAnalytics';

export type AccessRequestsCapabilitiesResult = AccessRequestsCapabilitiesInput & {
	resource: string;
	role: Role;
	traceId?: string;
	userId: string;
} & (
		| { capability: AccessCapability; error: null; result: AccessMode }
		| { capability: null; error: Error; result: null }
	);

/**
 * ACCESS_EXISTS: User should not proceed to request access. Consider redirecting to product since they already access.
 * CAN_JOIN: User can join the product.
 * CAN_REQUEST: User can request access to the product.
 * CANNOT_REQUEST_ACCESS: User should not proceed to request access. User cannot request or join the product.
 * VERIFICATION_REQUIRED: User should not proceed to request access. User needs to complete verification to join the product.
 */
type AccessCapability =
	| 'ACCESS_EXISTS'
	| 'CAN_JOIN'
	| 'CAN_REQUEST'
	| 'CANNOT_REQUEST_ACCESS'
	| 'VERIFICATION_REQUIRED';

type AccessMode =
	| 'ACCESS_EXISTS'
	| 'DIRECT_ACCESS'
	| 'PENDING_REQUEST_EXISTS'
	| 'APPROVED_REQUEST_EXISTS'
	| 'DENIED_REQUEST_EXISTS'
	| 'REQUEST_ACCESS'
	| 'FORBIDDEN';

type VerificationStatus = 'REQUIRED' | 'NOT_REQUIRED';

type Role = 'product/member' | string;

type AnalyticsAttributes = {
	accessMode: AccessMode;
	actorAaid: string;
	integration: string;
	roleName: Role;
	sourceProduct: Product;
	sourceTenantId: string;
	targetProduct: Product;
	targetTenantId: string;
	touchpoint: EnablementTouchpoint;
	traceId?: string;
	userAaid: string;
};

export const mapAccessModeToCapability = (
	accessMode: AccessMode,
	verificationStatus: VerificationStatus,
): AccessCapability => {
	switch (accessMode) {
		case 'ACCESS_EXISTS':
			return 'ACCESS_EXISTS';
		case 'DIRECT_ACCESS':
			return verificationStatus === 'REQUIRED' ? 'VERIFICATION_REQUIRED' : 'CAN_JOIN';
		case 'REQUEST_ACCESS':
			return verificationStatus === 'REQUIRED' ? 'VERIFICATION_REQUIRED' : 'CAN_REQUEST';
		default:
			return 'CANNOT_REQUEST_ACCESS';
	}
};

export type AccessRequestsCapabilitiesResponse = {
	resultsV2: {
		[resource: string]: Array<{
			accessMode: AccessMode;
			role: Role;
		}>;
	};
	verificationStatus: VerificationStatus;
};

// Input - data directly needed to join/raise a specific RA:
export type AccessRequestsCapabilitiesInput = {
	cloudId?: string; // If not given, defaults to dependencies.getCurrentCloudId()
	getAdditionalAttributes?: GetAdditionalAttributes<AccessRequestsCapabilitiesResult>;
	integration: string;
	product: Product;
	role?: Role; // If not given, defaults to 'product/member'
	touchpoint: EnablementTouchpoint;
};

// Dependencies
const dependencies: {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
		AbstractServiceMarker;
	fireErrorAnalytics?: (input: AnalyticsPayload) => Promise<void>;
	getAnalyticsChannel: typeof getAnalyticsChannel;
	getCurrentCloudId: (() => string) & AbstractServiceMarker;
	getCurrentProduct: (() => Product) & AbstractServiceMarker;
	getCurrentUserId: (() => string) & AbstractServiceMarker;
	getStargateURL: (url: `/${string}`) => string;
} = {
	fetchJSON,
	createAnalyticsEvent,
	getAnalyticsChannel,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getStargateURL,
};

type Deps = typeof dependencies;

export const fetchAccessRequestsCapabilities: typeof fetchAccessRequestsCapabilitiesImpl & {
	dependencies: {
		createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
		fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
			AbstractServiceMarker;
		fireErrorAnalytics?: (input: AnalyticsPayload) => Promise<void>;
		getAnalyticsChannel: typeof getAnalyticsChannel;
		getCurrentCloudId: (() => string) & AbstractServiceMarker;
		getCurrentProduct: (() => Product) & AbstractServiceMarker;
		getCurrentUserId: (() => string) & AbstractServiceMarker;
		getStargateURL: (url: `/${string}`) => string;
	};
} = Object.assign(fetchAccessRequestsCapabilitiesImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function fetchAccessRequestsCapabilitiesImpl(
	input: AccessRequestsCapabilitiesInput,
	context: DependenciesContext<Deps>,
): Promise<AccessRequestsCapabilitiesResult> {
	const deps = context.dependencies();
	const cloudId = input.cloudId ?? deps.getCurrentCloudId();
	const userId = deps.getCurrentUserId();
	const role = input.role ?? 'product/member';
	let result: AccessRequestsCapabilitiesResult;
	const resource = AnyAri.create({
		resourceType: 'site',
		resourceId: cloudId,
		resourceOwner: input.product,
	}).toString();
	try {
		const url = deps.getStargateURL(
			`/invitations/v1/access-requests/capabilities?resource=${resource}`,
		);
		const response = await deps.fetchJSON<AccessRequestsCapabilitiesResponse>({
			url,
		});
		result = mapAccessRequestsCapabilitiesResponse(response.data, {
			...input,
			resource,
			userId,
			role,
			traceId: response.traceId,
		});
	} catch (error) {
		result = {
			error: toError(error),
			...input,
			resource,
			userId,
			role,
			result: null,
			capability: null,
		};
	}
	fireAccessRequestsCapabilitiesAnalytics(result, context);
	return result;
}

function fireAccessRequestsCapabilitiesAnalytics(
	result: AccessRequestsCapabilitiesResult,
	context: DependenciesContext<Deps>,
) {
	const {
		error,
		result: accessMode,
		product,
		integration,
		role,
		userId,
		touchpoint,
		traceId,
		getAdditionalAttributes,
	} = result;
	const targetTenantId = result.cloudId ?? context.dependencies().getCurrentCloudId();
	const baseAttributes = {
		...(getAdditionalAttributes?.(result) ?? {}),
		actorAaid: userId,
		integration,
		roleName: role,
		sourceProduct: context.dependencies().getCurrentProduct(),
		sourceTenantId: context.dependencies().getCurrentCloudId(),
		targetProduct: product,
		targetTenantId,
		touchpoint,
		...(traceId ? { traceId } : {}),
		userAaid: userId,
	};

	if (error) {
		fireAccessRequestsCapabilitiesFailed(baseAttributes, context, error);
		return;
	}

	fireAccessRequestsCapabilitiesSucceeded({ ...baseAttributes, accessMode }, context);
	return;
}

function fireAccessRequestsCapabilitiesSucceeded(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	const deps = context.dependencies();
	deps
		.createAnalyticsEvent({
			eventType: OPERATIONAL_EVENT_TYPE,
			actionSubject: 'fetchAccessRequestsCapabilities',
			action: 'succeeded',
			source: attributes.touchpoint,
			attributes,
			tags: ['growth'],
		} satisfies GasCorePayload)
		.fire(deps.getAnalyticsChannel());
}

function fireAccessRequestsCapabilitiesFailed(
	attributes: Omit<AnalyticsAttributes, 'accessMode'>,
	context: DependenciesContext<Deps>,
	error: Error,
) {
	sharedFireErrorAnalytics(
		{
			error,
			meta: { id: 'fetchAccessRequestsCapabilities' },
			attributes,
		},
		context,
	);
}

function mapAccessRequestsCapabilitiesResponse(
	response: AccessRequestsCapabilitiesResponse,
	input: AccessRequestsCapabilitiesInput & {
		resource: string;
		role: Role;
		traceId?: string;
		userId: string;
	},
): AccessRequestsCapabilitiesResult {
	const result = response.resultsV2[input.resource]?.find(
		({ role }) => role === input.role,
	)?.accessMode;
	if (!result) {
		return {
			error: new Error(
				`No access request capability found for the given resource: ${input.resource} and role: ${input.role}`,
			),
			result: null,
			capability: null,
			...input,
		};
	}
	return {
		error: null,
		result,
		capability: mapAccessModeToCapability(result, response.verificationStatus),
		...input,
	};
}
